import React from "react";

// The Navigator's ambient backdrop.
//
// The original dark-page animation re-tuned for the light ground: a soft
// blurred grid, a slow light sweep (9s) and two drifting glows (22s/26s), with
// the palette moved from white-on-near-black to royal-and-ink-on-paper at much
// lower alpha — light grounds carry far less contrast latitude. The rotating
// conic wedge from the dark version is deliberately gone; its hard edges read
// as a rendering artifact on paper rather than as atmosphere.
export default function NavigatorBackdrop() {
  return (
    // z-0 rather than a negative z-index: the wrapper paints an opaque
    // bg-paper, and a negative-z child would be painted underneath it.
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Base wash */}
      <div className="absolute inset-0 bg-paper" />
      <div className="absolute inset-0 bg-[radial-gradient(900px_700px_at_50%_8%,#FFFFFF_0%,transparent_55%),radial-gradient(900px_600px_at_85%_115%,#E6EAF1_0%,transparent_62%),radial-gradient(700px_500px_at_8%_100%,#EAEEF4_0%,transparent_62%)]" />

      {/* Fine grid. Each line is a soft gradient band that fades in and out
          over ~3px rather than a hard 1px rule, then blurred, so the grid
          reads as atmosphere instead of graph paper. */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(20,51,107,0) 0px, rgba(20,51,107,.085) 7px, rgba(20,51,107,0) 14px, rgba(20,51,107,0) 58px), repeating-linear-gradient(90deg, rgba(20,51,107,0) 0px, rgba(20,51,107,.085) 7px, rgba(20,51,107,0) 14px, rgba(20,51,107,0) 58px)",
          filter: "blur(9px)",
          maskImage: "radial-gradient(90% 90% at 50% 45%, black 45%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(90% 90% at 50% 45%, black 45%, transparent 100%)",
        }}
      />

      {/* Light sweep */}
      <div
        className="nav-bd absolute inset-0"
        style={{
          background:
            "linear-gradient(100deg, rgba(29,78,216,0) 0%, rgba(29,78,216,0.055) 40%, rgba(29,78,216,0) 70%)",
          backgroundSize: "180% 100%",
          animation: "navSweep 9s linear infinite",
          willChange: "background-position",
        }}
      />

      {/* Drifting glows */}
      <div
        className="nav-bd absolute h-[44vmax] w-[44vmax] -top-1/3 -left-1/4 rounded-full blur-2xl"
        style={{
          background: "radial-gradient(circle at 50% 50%, rgba(29,78,216,0.13), transparent 60%)",
          animation: "navBlobA 22s ease-in-out infinite",
          willChange: "transform",
        }}
      />
      <div
        className="nav-bd absolute h-[36vmax] w-[36vmax] -bottom-1/3 -right-1/4 rounded-full blur-2xl"
        style={{
          background: "radial-gradient(circle at 50% 50%, rgba(96,165,250,0.16), transparent 60%)",
          animation: "navBlobB 26s ease-in-out infinite",
          willChange: "transform",
        }}
      />

      <style>{`
        @keyframes navSweep { 0% { background-position: -60% 0; } 100% { background-position: 160% 0; } }
        @keyframes navBlobA {
          0%, 100% { transform: translate3d(0,0,0) scale(1); }
          50% { transform: translate3d(4%, -3%, 0) scale(1.02); }
        }
        @keyframes navBlobB {
          0%, 100% { transform: translate3d(0,0,0) scale(1); }
          50% { transform: translate3d(-4%, 5%, 0) scale(1.02); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nav-bd { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
